import { ApiChartData, ChartLibraryData } from "../types";

interface SelectOption {
  value: string;
  label: string;
}

interface QueryStatusData {
  QUERY_STATUS_ID: string;
  STATUS: string;
  STATUS_CATEGORY: string;
}
interface TopicData {
  TOPIC_ID: string;
  TOPIC: string;
}
/**
 * Converts API chart data to the format required by the charting library
 * Transforms date format from YYYY-MM to YYYY-WMM pattern
 * @param apiData - The data from the API
 * @param targetLabelCount - Number of labels to generate in output (default: 12)
 * @returns Data structured for the charting library
 */
export function convertChartDataFormat(
  apiData: ApiChartData
): ChartLibraryData {
  // If specific target labels are provided, use those
  // Otherwise generate a 12-item array by cycling through input labels
  const targetLabelCount = apiData.x_axis.labels.length;
  // Validate input
  if (
    !apiData ||
    !apiData.x_axis ||
    !apiData.x_axis.labels ||
    !apiData.series
  ) {
    throw new Error("Invalid API data structure");
  }

  // Transform labels from YYYY-MM to YYYY-WMM format
  const formattedLabels = apiData.x_axis.labels.map((label) => {
    // Handle different potential input formats
    if (label.includes("-W")) {
      // Already in the right format
      return label;
    } else if (label.match(/^\d{4}-\d{2}$/)) {
      // Convert YYYY-MM to YYYY-WMM
      const [year, month] = label.split("-");
      return `${year}-W${month}`;
    } else {
      // For any other format, return as is
      return label;
    }
  });

  // Create expanded labels array
  let resultLabels: any[] = [];

  if (apiData.x_axis.labels.length) {
    // Use provided target labels if available
    resultLabels = apiData.x_axis.labels;
  } else {
    // Otherwise generate by cycling through available labels
    resultLabels = [];
    for (let i = 0; i < targetLabelCount; i++) {
      resultLabels.push(formattedLabels[i % formattedLabels.length]);
    }
  }

  // Transform each series into a dataset
  const datasets = apiData.series.map((series) => {
    // Expand the data array to match the target label count
    const expandedData: number[] = [];
    for (let i = 0; i < targetLabelCount; i++) {
      expandedData.push(series.data[i % series.data.length]);
    }

    return {
      label: series.name,
      data: expandedData,
      backgroundColor: series.color,
    };
  });
  console.log(datasets, "datasets");

  return {
    labels: resultLabels,
    datasets,
  };
}

/**
 * API datetimes like "2026-06-18 11:30:00" are UTC. Without a timezone suffix,
 * JavaScript would treat them as browser-local time and shift ET display (~5.5h off in IST).
 */
export const parseApiDateTime = (input?: string | null): Date | null => {
  if (!input || typeof input !== "string") return null;

  const trimmed = input.trim();
  if (!trimmed) return null;

  const hasTimezone = /[zZ]|[+-]\d{2}(:?\d{2})?$/.test(trimmed);
  const hasTime = /[ T]\d{2}:\d{2}/.test(trimmed);

  let isoString = trimmed;
  if (hasTime && !hasTimezone) {
    isoString = trimmed.includes("T")
      ? `${trimmed}Z`
      : `${trimmed.replace(" ", "T")}Z`;
  }

  const date = new Date(isoString);
  return isNaN(date.getTime()) ? null : date;
};

/**
 * Format a date string into the desired format using plain JavaScript.
 * @param dateStr - Input date string (e.g. "2023-12-18 00:00:00")
 * @param format - Format string using DD, MM, YYYY (e.g. "DD-MM-YYYY")
 * @param timeZone - Optional IANA timezone for calendar date (e.g. "America/New_York")
 * @returns Formatted date string
 */
export const formatDate = (
  dateStr: string,
  format: string,
  timeZone?: string
): string => {
  if (!dateStr) return "";

  const date = parseApiDateTime(dateStr);
  if (!date) return "";

  const pad = (num: number) => num.toString().padStart(2, "0");

  if (timeZone) {
    const parts = Object.fromEntries(
      new Intl.DateTimeFormat("en-US", {
        timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      })
        .formatToParts(date)
        .map((part) => [part.type, part.value])
    );

    const replacements: Record<string, string> = {
      DD: parts.day ?? "",
      MM: parts.month ?? "",
      YYYY: parts.year ?? "",
    };

    return format.replace(/DD|MM|YYYY/g, (match) => replacements[match] ?? match);
  }

  const replacements: Record<string, string> = {
    DD: pad(date.getDate()),
    MM: pad(date.getMonth() + 1),
    YYYY: date.getFullYear().toString(),
  };

  return format.replace(/DD|MM|YYYY/g, (match) => replacements[match]);
};

export const calculateAging = (createdDate: string): number => {
  try {
    // Parse the created date (format: "2024-11-04 05:37:30")
    const created = new Date(createdDate);

    // Add 3 days to the created date
    const thresholdDate = new Date(created);
    thresholdDate.setDate(created.getDate() + 3);

    // Calculate difference between current date and threshold date
    const currentDate = new Date();
    const diffTime = currentDate.getTime() - thresholdDate.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    // Return positive number (how many days overdue) or 0 if not overdue
    return Math.max(0, diffDays);
  } catch (error) {
    console.error("Error calculating aging for date:", createdDate, error);
    return 0;
  }
};

export function formatTimeWithZoneAbbreviation(
  input?: string,
  timeZone?: string,
  customAbbreviation?: string
): string {
  if (!input || typeof input !== "string") return "--";

  const date = parseApiDateTime(input);
  if (!date) return "--";

  const fallbackTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const targetTimeZone = timeZone || fallbackTimeZone;

  try {
    const formatter = new Intl.DateTimeFormat("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      timeZone: targetTimeZone,
      timeZoneName: "short",
    });

    const parts = formatter.formatToParts(date);

    const hour = parts.find((p) => p.type === "hour")?.value ?? "00";
    const minute = parts.find((p) => p.type === "minute")?.value ?? "00";
    const dayPeriod = parts.find((p) => p.type === "dayPeriod")?.value ?? "";
    const extractedAbbrev =
      parts.find((p) => p.type === "timeZoneName")?.value ?? "";

    const finalAbbreviation = customAbbreviation || extractedAbbrev;

    return `${hour}:${minute} ${dayPeriod} ${finalAbbreviation}`;
  } catch (error) {
    console.error("Time formatting failed:", error);
    return "--";
  }
}

export const convertToSelectOptions = <
  T extends QueryStatusData | TopicData
>(response: {
  data: T[];
} | T[] | null | undefined): SelectOption[] => {
  // Accept either the raw array or an object wrapping it in `data` -
  // some callers already unwrap the response before passing it in.
  const list = Array.isArray(response) ? response : response?.data;

  if (!Array.isArray(list)) {
    return [];
  }

  return list.map((item) => {
    if ("QUERY_STATUS_ID" in item) {
      return {
        value: item?.QUERY_STATUS_ID,
        label: item?.STATUS,
      };
    } else if ("TOPIC_ID" in item) {
      return {
        value: item?.TOPIC_ID,
        label: item?.TOPIC,
      };
    }
    throw new Error("Invalid data format");
  });
};
export const extractMrnFromPatient = (patientData: any): string | null => {
  if (!patientData?.identifier) {
    return null;
  }

  // Find the identifier with type "MRN"
  const mrnIdentifier = patientData?.identifier?.find(
    (identifier: any) => identifier?.type?.text === "MRN"
  );

  return mrnIdentifier?.value || null;
};

export function formatDateWithZoneAbbreviation(
  timestamp?: string | null,
  timeZone?: string,
  customAbbreviation?: string
): string {
  if (!timestamp || typeof timestamp !== "string") return "";

  const date = parseApiDateTime(timestamp);
  if (!date) return "";

  const fallbackTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const targetTimeZone = timeZone || fallbackTimeZone;

  try {
    const formatter = new Intl.DateTimeFormat("en-US", {
      weekday: "short",
      month: "short",
      day: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
      timeZone: targetTimeZone,
      timeZoneName: "short",
    });

    const parts = formatter.formatToParts(date);
    const map = Object?.fromEntries(parts?.map((p) => [p.type, p.value]));
    const extractedAbbrev =
      parts.find((p) => p.type === "timeZoneName")?.value ?? "";
    const finalAbbreviation = customAbbreviation || extractedAbbrev;

    return `${map?.weekday}, ${map?.month} ${map?.day}, ${map?.year} at ${map?.hour}:${map?.minute} ${map?.dayPeriod} ${finalAbbreviation}`;
  } catch (error) {
    console.error("Date formatting failed:", error);
    return "";
  }
}
